// src/pages/Admin/Chat/ChatPageAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  getAllMessages,
  deleteMessage,
} from "./../../../services/chatServices";
import { getGroupsHome } from "./../../../services/groupServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import Button from "./../../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import { FaFilter, FaSearch, FaComments } from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function ChatPageAdmin() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [messages, setMessages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupColors, setGroupColors] = useState({});

  const [loading, setLoading] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [searchUser, setSearchUser] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllMessages();
      setMessages(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erreur lors du chargement des messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const data = await getGroupsHome();
      setGroups(Array.isArray(data) ? data : []);
      const colors = (Array.isArray(data) ? data : []).reduce((acc, g) => {
        acc[g.nom] = g.color_groupe; // hex / tailwind / nom — on stylisera inline si hex
        return acc;
      }, {});
      setGroupColors(colors);
    } catch {
      toast.error("Erreur lors du chargement des groupes.");
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchGroups();
  }, [fetchAll, fetchGroups]);

  const filteredMessages = useMemo(() => {
    let out = messages;
    if (searchUser.trim()) {
      const q = searchUser.trim().toLowerCase();
      out = out.filter((m) =>
        `${m?.Users?.nom || ""} ${m?.Users?.prenom || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (selectedGroup) {
      out = out.filter((m) => m?.group?.nom === selectedGroup);
    }
    return out;
  }, [messages, searchUser, selectedGroup]);

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce message ?"))
      return;
    try {
      await deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.success("Message supprimé avec succès.");
    } catch {
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  const resetFilters = () => {
    setSearchUser("");
    setSelectedGroup("");
  };

  const badgeStyle = (nomGroupe) => {
    const col = groupColors[nomGroupe];
    if (!col) return {};
    // Si on reçoit un code couleur (ex: #AABBCC), on l'applique inline
    if (col.startsWith("#") || col.startsWith("rgb")) {
      return { backgroundColor: col };
    }
    // Sinon, on tente une classe Tailwind si tu en stockes (ex: "bg-emerald-500")
    return {};
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />
      <ScrollIndicator />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Gestion des canaux de discussion"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau supérieur : titre + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaComments />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Modérer les messages par groupe
                  </p>
                  <p className="text-white/90 text-sm">
                    Supprime les messages inappropriés et filtre par groupe ou
                    par utilisateur.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  text="Réinitialiser les filtres"
                  icon={FaFilter}
                  onClick={resetFilters}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 sm:px-8 py-5 border-b border-slate-200 dark:border-slate-800">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Recherche utilisateur */}
              <div className="col-span-1">
                <label className="sr-only" htmlFor="search-user">
                  Rechercher par nom
                </label>
                <div className="relative">
                  <input
                    id="search-user"
                    type="text"
                    placeholder="Rechercher par nom…"
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              {/* Sélecteur de groupe */}
              <div className="col-span-1">
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">
                    {loadingGroups
                      ? "Chargement des groupes…"
                      : "— Sélectionner un groupe —"}
                  </option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.nom}>
                      {g.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Compteur / info */}
              <div className="col-span-1 flex items-center justify-start sm:justify-end">
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  {filteredMessages.length} message
                  {filteredMessages.length > 1 ? "s" : ""} trouvé
                  {selectedGroup ? ` dans ${selectedGroup}` : ""}.
                </span>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filteredMessages.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun message à afficher
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajuste tes filtres ou essaie une autre recherche.
                </p>
              </div>
            )}

            {/* Table desktop */}
            {!loading && filteredMessages.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {["Détails", "Actions"].map((h, idx) => (
                            <th
                              key={h}
                              className={`px-4 py-3 text-left font-semibold text-slate-700 dark:text-white ${
                                idx === 0 ? "w-[80%]" : "w-[20%]"
                              }`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filteredMessages.map((m) => {
                          const userName = `${m?.Users?.nom || ""} ${
                            m?.Users?.prenom || ""
                          }`.trim();
                          const groupe = m?.group?.nom || "Groupe";
                          const created = m?.createdAt
                            ? new Date(m.createdAt).toLocaleString("fr-FR")
                            : null;

                          return (
                            <tr
                              key={m.id}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-start gap-3">
                                  {/* Badge / avatar initiales */}
                                  <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-100 font-semibold select-none">
                                    {userName ? userName[0] : "U"}
                                  </div>

                                  <div className="min-w-0">
                                    {/* Groupe */}
                                    <div
                                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                                      style={badgeStyle(groupe)}
                                    >
                                      {groupe}
                                    </div>

                                    {/* Auteur + date */}
                                    <div className="mt-1 text-slate-800 dark:text-slate-100 font-medium">
                                      {userName || "Utilisateur"}
                                      {created && (
                                        <span className="ml-2 text-[11px] text-slate-500 dark:text-slate-400">
                                          • {created}
                                        </span>
                                      )}
                                    </div>

                                    {/* Message */}
                                    <div className="mt-1 text-slate-700 dark:text-slate-300 break-words">
                                      {m?.message || "—"}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end">
                                  <ButtonDelete
                                    onClick={() => handleDeleteMessage(m.id)}
                                    hoverLabel="Supprimer"
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes mobile */}
            {!loading && filteredMessages.length > 0 && (
              <div className="md:hidden space-y-3">
                {filteredMessages.map((m) => {
                  const userName = `${m?.Users?.nom || ""} ${
                    m?.Users?.prenom || ""
                  }`.trim();
                  const groupe = m?.group?.nom || "Groupe";
                  const created = m?.createdAt
                    ? new Date(m.createdAt).toLocaleString("fr-FR")
                    : null;

                  return (
                    <div
                      key={`card-${m.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-100 font-semibold select-none">
                          {userName ? userName[0] : "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                                style={badgeStyle(groupe)}
                              >
                                {groupe}
                              </span>
                              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {userName || "Utilisateur"}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-500 dark:text-slate-400">
                              {created || ""}
                            </div>
                          </div>

                          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 break-words">
                            {m?.message || "—"}
                          </p>

                          <div className="mt-3 flex items-center justify-end">
                            <ButtonDelete
                              onClick={() => handleDeleteMessage(m.id)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>
    </section>
  );
}
